import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Stack, Tab, Tabs, useTheme } from '@mui/material';
import _uniq from 'lodash/uniq';
import { ObjectSchema } from 'yup';

import {
  FullTalentQuestionDTO,
  FullTalentQuestionDTOType,
  getGetOrganizationTalentQuestionsQueryKey,
  PlatformLocale,
  TalentQuestionsResponseDTO,
  useGetOrganizationTalentQuestions,
  usePostOrganizationTalentQuestions
} from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import { CommonFormFooter } from '@/components/Form/Form';
import { WarningProvider } from '@/hooks/useWarnings';
import {
  asPath,
  TALENT_REGISTRATION_FORM_DEFAULT_LANGUAGE,
  TALENT_REGISTRATION_FORM_LANGUAGES,
  TALENT_REGISTRATION_FORM_TABS,
  TALENT_REGISTRATION_FORM_TABS_LIST,
  toOrganizationSettingsTalentRegistrationForm
} from '@/root/linker';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';
import { shortId } from '@/utils/shortId';
import SettingsModalForm from '@/views/SettingsModalForm/SettingsModalForm';

import AddQuestionButton from './AddQuestionButton';
import { DEFAULT_LANGUAGE_CODE } from './constants';
import LanguageListSection from './LanguageListSection';
import { getTalentRegistrationFormValidationSchema, getTalentRegistrationFormWarningSchema } from './schema';
import TalentRegistrationFormContent, { TalentRegistrationFormInstance } from './TalentRegistrationFormContent';

type InvitationLanguage = Values<typeof PlatformLocale>;

const DEFAULT_LANGUAGE = TALENT_REGISTRATION_FORM_DEFAULT_LANGUAGE as InvitationLanguage;

interface TalentRegistrationFormProps {
  parentRoutePattern: string;
}

const TalentRegistrationForm: FC<TalentRegistrationFormProps> = ({ parentRoutePattern }) => {
  const queryClient = useQueryClient();

  const { spacing } = useTheme();
  const { t } = useTranslation(['settings', 'validation']);
  const activeFormContentRef = useRef<TalentRegistrationFormInstance>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<InvitationLanguage>(DEFAULT_LANGUAGE);
  const [activeLanguages, setActiveLanguages] = useState<InvitationLanguage[]>([]);
  const navigate = useNavigate();
  const params = useParams() as { organizationSlug: string; activeTab: TALENT_REGISTRATION_FORM_TABS };
  const { activeTab, organizationSlug } = params || {};

  const { data: talentQuestions, isLoading } = useGetOrganizationTalentQuestions<TalentQuestionsResponseDTO, object>(
    organizationSlug
  );
  const { mutateAsync: updateTalentQuestions } = usePostOrganizationTalentQuestions();

  const handleSave = useCallback(
    async (data: TalentQuestionsResponseDTO) => {
      await updateTalentQuestions({ organizationSlug, data });
      await queryClient.invalidateQueries(getGetOrganizationTalentQuestionsQueryKey(organizationSlug));
    },
    [updateTalentQuestions, organizationSlug, queryClient]
  );

  const handleAddQuestion = useCallback(() => {
    activeFormContentRef.current?.addQuestion({
      id: shortId(),
      type: FullTalentQuestionDTOType.TEXT,
      isRequired: true,
      isTalentEditable: false,
      isActive: true,
      isKnockout: false
    } as FullTalentQuestionDTO);
  }, []);

  const onClose = useCallback(() => {
    navigate(asPath(parentRoutePattern, params));
  }, [navigate, params, parentRoutePattern]);

  useEffect(() => {
    if (!talentQuestions) return;

    setActiveLanguages(
      _uniq([DEFAULT_LANGUAGE_CODE, ...Object.keys(talentQuestions?.translations || {})]) as InvitationLanguage[]
    );
  }, [talentQuestions]);

  const warningValidationSchema = useMemo(() => getTalentRegistrationFormWarningSchema(), []);

  if (isLoading || !talentQuestions) return <BackdropLoader />;

  return (
    <SettingsModalForm
      head={
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {t('orgSettings.talentRegistrationForm.title')}
          <AddQuestionButton onAddQuestion={handleAddQuestion} />
        </Stack>
      }
      defaultValues={talentQuestions!}
      onSave={handleSave}
      onClose={onClose}
      validation={getTalentRegistrationFormValidationSchema(t) as ObjectSchema<TalentQuestionsResponseDTO>}
      routePattern={`${parentRoutePattern}/form`}
    >
      <WarningProvider warningSchema={warningValidationSchema}>
        <Stack direction="row" sx={{ flexGrow: 1, flexBasis: 0 }}>
          <Stack px={2} py={3} height="100%" bgcolor="background.default" sx={{ textAlign: 'center' }}>
            <LanguageListSection
              value={activeLanguages}
              onChange={languages => setActiveLanguages(languages as InvitationLanguage[])}
              onSelectedChange={selectedLanguage => setSelectedLanguage(selectedLanguage as InvitationLanguage)}
              selectedValue={selectedLanguage}
              options={TALENT_REGISTRATION_FORM_LANGUAGES}
              validation={getTalentRegistrationFormValidationSchema(t) as ObjectSchema<TalentQuestionsResponseDTO>}
            />
          </Stack>
          <Stack p={2} pt={0} flexGrow={1} bgcolor="grey.100" minHeight="100%" overflow="auto">
            <Tabs
              value={activeTab}
              sx={{
                mt: 2,
                minHeight: spacing(5),
                position: 'sticky',
                top: spacing(2),
                zIndex: 1,
                bgcolor: 'grey.100',
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  width: '100%',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  height: spacing(2),
                  bgcolor: 'grey.100'
                },
                '& .MuiTab-root': {
                  padding: spacing(1.5, 2),
                  minHeight: 'unset',
                  textTransform: 'none',
                  alignItems: 'start',
                  borderTopRightRadius: spacing(0.5),
                  borderTopLeftRadius: spacing(0.5),
                  lineHeight: 1
                },
                '& .MuiTab-root:not(.Mui-selected)': {
                  fontWeight: typography.fontWeightRegular
                },
                '& .MuiTab-root.Mui-selected': {
                  fontWeight: typography.fontWeightMedium,
                  bgcolor: 'primary.100'
                },
                '& .MuiTabs-flexContainer': {
                  borderBottom: borders.default,
                  borderColor: 'grey.400',
                  gap: 3
                }
              }}
            >
              {TALENT_REGISTRATION_FORM_TABS_LIST.map(formKey => (
                <Tab
                  label={t(`orgSettings.talentRegistrationForm.${formKey}`)}
                  value={formKey}
                  key={formKey}
                  component={Link}
                  to={toOrganizationSettingsTalentRegistrationForm({ organizationSlug, activeTab: formKey })}
                />
              ))}
            </Tabs>
            {TALENT_REGISTRATION_FORM_TABS_LIST.map(
              formKey =>
                activeTab === formKey && (
                  <TalentRegistrationFormContent
                    key={formKey}
                    ref={activeFormContentRef}
                    activeTab={formKey}
                    languages={activeLanguages}
                    activeLanguage={selectedLanguage}
                    tabKeys={TALENT_REGISTRATION_FORM_TABS_LIST}
                    organizationSlug={organizationSlug}
                  />
                )
            )}
          </Stack>
        </Stack>
        <CommonFormFooter
          submitText={t('orgSettings.talentRegistrationForm.submit')}
          resetText={t('orgSettings.talentRegistrationForm.reset')}
          buttonsContainerSx={{ justifyContent: 'flex-end' }}
        />
      </WarningProvider>
    </SettingsModalForm>
  );
};

export default TalentRegistrationForm;
